/**
 * READING A RECIPE — the schema, the clamp, and the rules the shape cannot say.
 *
 * Split from `sense-recipe.ts` when that file crossed the size ratchet, along
 * the seam A-R84 named: the TYPE says what a recipe IS, and this says whether a
 * given one is one. They are two things a reader wants separately — somebody
 * writing a recipe reads the type and its comments, somebody debugging a
 * refusal reads these rules — and only this half needs zod.
 *
 * `validateSenseRecipe` is re-exported from the type file, so every importer,
 * every mock and every test that reached for it there still does.
 *
 * WHAT THIS IS FOR. A recipe is data, and one of them will be written by an
 * agent on a user's behalf and stored as a row. So the things that would let
 * such a recipe read more than it declared, spend more than it said, or lose
 * messages in silence are checked HERE, before anything runs: an undeclared
 * field path, an undeclared template slot, a cap raised past the rail's own, a
 * newest-first list with no way to drain it. The caps CLAMP rather than reject —
 * a recipe is not wrong for wanting 500 items a beat, it just does not get them
 * — while the rules that make a recipe WRONG refuse it with a sentence its
 * author can act on.
 */

import { z } from 'zod';
import {
  RAIL_SLOTS,
  REFS_SLOTS,
  SENSE_CAPS,
  type ArgValue,
  type FieldKind,
  type FieldPath,
  type SenseRecipe,
} from './sense-recipe.js';
// Straight from the walker, not through the type file that re-exports it: the
// validator already sits inside that file's load-time cycle, and there is no
// reason to make it longer.
import { templateSlots } from './sense-recipe-walk.js';


const argValueSchema: z.ZodType<ArgValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(argValueSchema),
    z.record(z.string(), argValueSchema),
  ]),
);

const argsSchema = z.record(z.string(), argValueSchema);

const fieldDeclSchema = z.strictObject({
  path: z.string().min(1),
  label: z.string().min(1),
  kind: z.enum(['id', 'time', 'person', 'text', 'link', 'number']),
});

const parseSchema = z.enum(['json', 'granola-markup']);

/**
 * STRICT EVERYWHERE. A recipe is hand-written or agent-written data, and the
 * failure a loose object gives you is the worst kind: `versonPath` is dropped in
 * silence, the card ids collide, and nothing anywhere says a key was misspelled.
 * `args` stays a record — argument names belong to the provider, not to us.
 */
const recipeSchema = z.strictObject({
  name: z.string().min(1),
  version: z.number().int().nonnegative(),
  app: z.string().min(1),
  source: z.string().min(1),
  origin: z.enum(['shipped', 'user']),
  mode: z.enum(['poll', 'push']),
  item: z.strictObject({
    idPath: z.union([z.string(), z.array(z.string()).min(1)]),
    idPrefix: z.string().min(1).optional(),
    occurredAtPath: z.string(),
    versionPath: z.string().optional(),
  }),
  push: z
    .strictObject({
      triggerSlugs: z.array(z.string().min(1)).min(1),
      payloadItemPath: z.string(),
    })
    .optional(),
  list: z
    .strictObject({
      kind: z.enum(['tool', 'picked-corpus']).optional(),
      tool: z.string().min(1).optional(),
      args: argsSchema.optional(),
      itemsPath: z.string().optional(),
      cursor: z.union([
        z.strictObject({ kind: z.literal('occurred-at') }),
        z.strictObject({
          kind: z.literal('token'),
          path: z.string(),
          elsePath: z.string().optional(),
        }),
      ]),
      startToken: z
        .strictObject({ tool: z.string().min(1), args: argsSchema, path: z.string() })
        .optional(),
      expand: z
        .strictObject({
          tool: z.string().min(1),
          args: argsSchema,
          itemsPath: z.string(),
          maxRows: z.number().int().positive(),
        })
        .optional(),
      drain: z
        .strictObject({
          arg: z.string().min(1),
          from: z.union([
            z.strictObject({ row: z.string().min(1) }),
            z.strictObject({ answer: z.string().min(1) }),
            z.strictObject({
              page: z.strictObject({ at: z.string().min(1), of: z.string().min(1) }),
            }),
          ]),
          maxPages: z.number().int().positive(),
        })
        .optional(),
      windowGranularity: z.literal('day').optional(),
      derive: z
        .strictObject({
          label: z.string().min(1),
          fromLink: z.string().min(1),
          param: z.string().min(1),
          elsePath: z.string().min(1).optional(),
        })
        .optional(),
      skipWhere: z
        .array(
          z.union([
            z.strictObject({ path: z.string().min(1), equalsSlot: z.literal('selfId') }),
            z.strictObject({
              path: z.string().min(1),
              equals: z.union([z.string(), z.number(), z.boolean()]),
            }),
            z.strictObject({ path: z.string().min(1), present: z.literal(true) }),
          ]),
        )
        .min(1)
        .max(4)
        .optional(),
      morePath: z.string().min(1).optional(),
      windowBackDays: z.number().int().positive().optional(),
      order: z.enum(['oldest-first', 'newest-first']),
      maxItemsPerBeat: z.number().int().positive(),
      firstBeatLookbackDays: z.number().int().positive().optional(),
      parse: parseSchema.optional(),
    })
    .optional(),
  read: z
    .strictObject({
      tool: z.string().min(1),
      args: argsSchema,
      bodyPath: z.string().optional(),
      maxReadsPerBeat: z.number().int().positive(),
      parse: parseSchema.optional(),
    })
    .optional(),
  resolve: z
    .array(
      z.strictObject({
        label: z.string().min(1),
        tool: z.string().min(1),
        args: argsSchema,
        path: z.string().min(1),
        kind: z.literal('person'),
      }),
    )
    .optional(),
  card: z.strictObject({
    title: z.string().min(1),
    what: z.string().min(1),
    who: z.array(z.string()),
    when: z.string(),
    gist: z.enum(['model', 'fields']),
    fieldsForGist: z.array(z.string()).optional(),
    link: z.string().optional(),
    refs: z.record(z.string(), z.string()),
  }),
  fields: z.array(fieldDeclSchema).min(1),
  legacyCursor: z
    .strictObject({ key: z.string().min(1), kind: z.enum(['epoch-ms', 'iso']) })
    .optional(),
  deeper: z
    .strictObject({
      tool: z.string().min(1),
      args: argsSchema,
      bodyPath: z.string(),
      maxChars: z.number().int().positive(),
      parse: z.enum(['json', 'granola-markup']).optional(),
      // A noun, not a sentence: it rides into a one-line result header.
      opens: z.string().min(1).max(24).optional(),
    })
    .optional(),
});

/** Every argument object a recipe carries, so one walk finds every slot. */
function argBlocks(recipe: SenseRecipe): Array<{ where: string; args: Record<string, ArgValue> }> {
  const blocks: Array<{ where: string; args: Record<string, ArgValue> }> = [];
  if (recipe.list?.args) blocks.push({ where: 'list.args', args: recipe.list.args });
  if (recipe.list?.startToken) {
    blocks.push({ where: 'list.startToken.args', args: recipe.list.startToken.args });
  }
  if (recipe.list?.expand) blocks.push({ where: 'list.expand.args', args: recipe.list.expand.args });
  if (recipe.read) blocks.push({ where: 'read.args', args: recipe.read.args });
  if (recipe.deeper) blocks.push({ where: 'deeper.args', args: recipe.deeper.args });
  return blocks;
}

/** Every string inside an `ArgValue` tree — where a template slot can hide. */
function argStrings(value: ArgValue, out: string[] = []): string[] {
  if (typeof value === 'string') out.push(value);
  else if (Array.isArray(value)) for (const entry of value) argStrings(entry, out);
  else if (value !== null && typeof value === 'object') {
    for (const entry of Object.values(value)) argStrings(entry, out);
  }
  return out;
}

/**
 * The recipe with the rail's ceilings applied, as a COPY. A recipe is never
 * refused for a cap, and the object handed in is never edited under its owner —
 * a shipped recipe is a module constant, and clamping one in place would change
 * it for every later reader of that module.
 */
function clampCaps(recipe: SenseRecipe): SenseRecipe {
  const clamped: SenseRecipe = { ...recipe };
  if (recipe.list) {
    clamped.list = {
      ...recipe.list,
      maxItemsPerBeat: Math.min(recipe.list.maxItemsPerBeat, SENSE_CAPS.maxItemsPerBeat),
      // Absent stays absent: the executor reads "no window declared" as the
      // rail's own default, and clamping an undefined into a number here would
      // put the default in two places at once.
      ...(recipe.list.firstBeatLookbackDays === undefined
        ? {}
        : {
            firstBeatLookbackDays: Math.min(
              recipe.list.firstBeatLookbackDays,
              SENSE_CAPS.maxFirstBeatLookbackDays,
            ),
          }),
      // A-R87: a list may look back further than its cursor, but never further
      // than the rail would have read on its own.
      ...(recipe.list.windowBackDays === undefined
        ? {}
        : {
            windowBackDays: Math.min(
              recipe.list.windowBackDays,
              SENSE_CAPS.maxFirstBeatLookbackDays,
            ),
          }),
    };
  }
  if (recipe.read) {
    clamped.read = {
      ...recipe.read,
      maxReadsPerBeat: Math.min(recipe.read.maxReadsPerBeat, SENSE_CAPS.maxReadsPerBeat),
    };
  }
  if (recipe.fields.length > SENSE_CAPS.maxFields) {
    clamped.fields = recipe.fields.slice(0, SENSE_CAPS.maxFields);
  }
  if (recipe.resolve && recipe.resolve.length > SENSE_CAPS.maxResolvesPerItem) {
    clamped.resolve = recipe.resolve.slice(0, SENSE_CAPS.maxResolvesPerItem);
  }
  if (clamped.list?.expand) {
    clamped.list = {
      ...clamped.list,
      expand: {
        ...clamped.list.expand,
        maxRows: Math.min(clamped.list.expand.maxRows, SENSE_CAPS.maxExpandRowsPerBeat),
      },
    };
  }
  if (clamped.list?.drain) {
    clamped.list = {
      ...clamped.list,
      drain: {
        ...clamped.list.drain,
        maxPages: Math.min(clamped.list.drain.maxPages, SENSE_CAPS.maxDrainPages),
      },
    };
  }
  return clamped;
}

/**
 * WHAT A LABEL MAY BE. A label is a name in three places at once: a `{{slot}}`
 * inside a template, an entry in `approved_fields`, and one token of the
 * comma-joined answer the approval card sends back. A label carrying a comma
 * therefore splits into two tokens that match no field, and the person's tick is
 * dropped — in the safe direction, and in silence. An identifier keeps all three
 * readings the same one.
 */
const LABEL_SHAPE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/**
 * WHAT A USER RECIPE'S NAME AND APP MAY BE (A-R71) — the TABLE's own two
 * regexes, checked here so a bad shape costs nothing.
 *
 * `nami_sense_recipes` carries both as CHECK constraints. A name the table
 * refuses but this accepts fails in the worst possible order: the approval runs
 * a METERED first beat, deposits its cards and writes its cursor, and only then
 * dies at the insert. The same shapes also make the toolkit lookup in the
 * converge registry miss, so the app-level pause and resume 404.
 *
 * USER-LANE ONLY, and not out of leniency: a shipped recipe is code somebody
 * read, and its `app` is Nami's own short word for the sense (`drive`,
 * `calendar`) rather than a toolkit slug.
 */
/**
 * WHAT AN ID PREFIX MAY BE (A-R78). A cite id is discovered in prose by
 * `citesInText`, whose `ev:`-shaped token STOPS at whitespace, a backtick, or a
 * closing bracket or quote — so a prefix carrying one of those mints a citation
 * that cannot be found in the page that cites it. The safe alphabet is the one
 * every provider id in the tree already uses.
 */
const ID_PREFIX_SHAPE = /^[A-Za-z0-9._-]{1,64}$/;

const USER_NAME_SHAPE = /^[a-z0-9][a-z0-9-]{1,63}$/;
const USER_APP_SHAPE = /^[a-z0-9_]{1,64}$/;

/** Every tool a recipe calls, with the place that named it. */
function toolCalls(recipe: SenseRecipe): Array<{ where: string; tool: string }> {
  const calls: Array<{ where: string; tool: string }> = [];
  if (recipe.list?.tool) calls.push({ where: 'list.tool', tool: recipe.list.tool });
  if (recipe.list?.startToken) {
    calls.push({ where: 'list.startToken.tool', tool: recipe.list.startToken.tool });
  }
  if (recipe.list?.expand) calls.push({ where: 'list.expand.tool', tool: recipe.list.expand.tool });
  if (recipe.read) calls.push({ where: 'read.tool', tool: recipe.read.tool });
  recipe.resolve?.forEach((entry, i) => calls.push({ where: `resolve[${i}].tool`, tool: entry.tool }));
  if (recipe.deeper) calls.push({ where: 'deeper.tool', tool: recipe.deeper.tool });
  return calls;
}

/**
 * Read a recipe: is it one, and does it stay inside its own allowlist?
 *
 * Returns the CLAMPED recipe, not the input — the caps are applied before the
 * path checks, so a field the cap dropped is reported as undeclared rather than
 * quietly surviving into the executor.
 */
export function validateSenseRecipe(
  input: unknown,
): { ok: true; recipe: SenseRecipe } | { ok: false; errors: string[] } {
  const parsed = recipeSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map(
        (issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`,
      ),
    };
  }

  const recipe = clampCaps(parsed.data as SenseRecipe);
  const errors: string[] = [];
  const at = (message: string) => `sense recipe '${recipe.name}': ${message}`;

  // The allowlist, read two ways: paths for FieldPath positions, labels for slots.
  const railSlots = new Set<string>(RAIL_SLOTS);
  const byPath = new Set(recipe.fields.map((f) => f.path));
  const labelToPath = new Map<string, string>();
  for (const field of recipe.fields) {
    if (!LABEL_SHAPE.test(field.label)) {
      errors.push(
        at(
          `the field at '${field.path}' is labelled '${field.label}', which is not a name — a label is a letter and then letters, digits or underscores (at most 64), because the same word is a '{{slot}}' and a ticked option on the approval card`,
        ),
      );
    }
    if (railSlots.has(field.label)) {
      errors.push(
        at(
          `the field at '${field.path}' is labelled '${field.label}', which is the rail slot '{{${field.label}}}' — one slot, one meaning: label it something else and reach the field by its path`,
        ),
      );
    }
    if (labelToPath.has(field.label)) {
      errors.push(
        at(
          `two fields share the label '${field.label}' ('${labelToPath.get(field.label)}' and '${field.path}') — a template slot naming it could mean either`,
        ),
      );
    } else {
      labelToPath.set(field.label, field.path);
    }
  }

  // A-R34: a resolve's label is a VIRTUAL declared field — a path and a slot
  // both, since the value is attached to the item under that same name. It joins
  // the namespace here, so `card.who: ['author']` and `{{author}}` read as
  // declared; and it may collide with nothing, for the same reason two fields
  // may not share a label: one slot, one meaning.
  const declaredLabels = new Set(labelToPath.keys());
  for (const entry of recipe.resolve ?? []) {
    if (!LABEL_SHAPE.test(entry.label)) {
      errors.push(
        at(
          `the resolve is labelled '${entry.label}', which is not a name — a resolve's label is a declared field's label (A-R34), so it keeps the same shape: a letter and then letters, digits or underscores, at most 64`,
        ),
      );
      continue;
    }
    if (railSlots.has(entry.label)) {
      errors.push(
        at(
          `the resolve '${entry.label}' is named after the rail slot '{{${entry.label}}}' — one slot, one meaning: name it something else`,
        ),
      );
      continue;
    }
    if (labelToPath.has(entry.label)) {
      errors.push(
        at(
          `the resolve '${entry.label}' shares its name with a declared field (at '${labelToPath.get(entry.label)}') — the resolved person would land on top of it`,
        ),
      );
      continue;
    }
    // A LABEL IS ALSO A PATH HERE, because the resolved person is attached to the
    // item under that very name. So a resolve labelled `author` and a field
    // declared at the PATH `author` are the same key: the field would read the
    // rendered person instead of the payload's own value, silently, and only for
    // items the resolve happened to answer for.
    if (byPath.has(entry.label)) {
      const shadowed = recipe.fields.find((field) => field.path === entry.label);
      errors.push(
        at(
          `the resolve '${entry.label}' would be attached to the item at '${entry.label}', which is where the field '${shadowed?.label ?? entry.label}' already reads — one of the two must be named differently`,
        ),
      );
      continue;
    }
    byPath.add(entry.label);
    labelToPath.set(entry.label, entry.label);
  }

  // A-R79: THE DERIVED KEY IS A NAME LIKE A RESOLVE'S, and it may collide with
  // nothing for the same reason: one slot, one meaning. It is a PATH too, since
  // the value is attached to the row under that very name.
  const derive = recipe.list?.derive;
  if (derive) {
    if (!LABEL_SHAPE.test(derive.label)) {
      errors.push(
        at(
          `list.derive is labelled '${derive.label}', which is not a name — a letter and then letters, digits or underscores, at most 64`,
        ),
      );
    } else if (railSlots.has(derive.label)) {
      errors.push(
        at(
          `list.derive is named after the rail slot '{{${derive.label}}}' — one slot, one meaning: name it something else`,
        ),
      );
    } else if (labelToPath.has(derive.label) || byPath.has(derive.label)) {
      errors.push(
        at(
          `list.derive is named '${derive.label}', which a field already claims — the derived key would land on top of it`,
        ),
      );
    } else {
      byPath.add(derive.label);
      labelToPath.set(derive.label, derive.label);
    }
  }

  const requirePath = (where: string, path: FieldPath) => {
    if (byPath.has(path)) return;
    const asLabel = labelToPath.get(path);
    errors.push(
      at(
        asLabel
          ? `${where} is '${path}', which is a field LABEL — a path position names the declared path (did you mean '${asLabel}'?)`
          : `${where} is '${path}', which no field declares — every path a card reads must be in fields`,
      ),
    );
  };

  // A-R78: one path or several, every part is a declared path. The array form
  // is refused empty by the schema, so a part is always something.
  const idParts = Array.isArray(recipe.item.idPath) ? recipe.item.idPath : [recipe.item.idPath];
  idParts.forEach((path, i) => {
    requirePath(Array.isArray(recipe.item.idPath) ? `item.idPath[${i}]` : 'item.idPath', path);
  });
  if (recipe.item.idPrefix !== undefined && !ID_PREFIX_SHAPE.test(recipe.item.idPrefix)) {
    errors.push(
      at(
        `item.idPrefix is '${recipe.item.idPrefix}', which carries a character no citation can hold — a prefix is letters, digits, '.', '_' or '-', at most 64, because the cite id it heads is found in prose by where it STOPS`,
      ),
    );
  }
  requirePath('item.occurredAtPath', recipe.item.occurredAtPath);
  if (recipe.item.versionPath) requirePath('item.versionPath', recipe.item.versionPath);
  recipe.card.who.forEach((path, i) => requirePath(`card.who[${i}]`, path));
  requirePath('card.when', recipe.card.when);
  for (const [key, path] of Object.entries(recipe.card.refs)) {
    requirePath(`card.refs.${key}`, path);
  }
  recipe.card.fieldsForGist?.forEach((path, i) =>
    requirePath(`card.fieldsForGist[${i}]`, path),
  );
  // A-R38: the value a row is dropped on is read off the row, so it is declared
  // like every other path — an undeclared one reads as empty, which equals
  // nothing and silently turns the rule off.
  recipe.list?.skipWhere?.forEach((rule, i) =>
    requirePath(`list.skipWhere[${i}].path`, rule.path),
  );
  // A-R91: the row value that becomes the next page's upper bound is read off a
  // listed row, so it is a declared path like every other; and the argument it
  // fills must not be one the recipe already sets, or the drain would fight the
  // recipe over the same key on every page but the first.
  if (recipe.list?.drain) {
    // A ROW SOURCE READS A LISTED ROW, so it is a declared path like every other.
    // The other two read the ANSWER's envelope, which `itemsPath` and `morePath`
    // walk too and which no field declares: they say where the paging lives, not
    // what a card keeps.
    const from = recipe.list.drain.from;
    if ('row' in from) requirePath('list.drain.from.row', from.row);
    if (Object.hasOwn(recipe.list.args ?? {}, recipe.list.drain.arg)) {
      errors.push(
        at(
          `list.drain fills '${recipe.list.drain.arg}', which list.args already sets — the drain would overwrite the recipe's own argument from the second page on`,
        ),
      );
    }
  }
  // A-R79: the link a key is read out of is declared too, and so is the path
  // that answers when the link's parameter holds nothing.
  if (derive) {
    requirePath('list.derive.fromLink', derive.fromLink);
    if (derive.elsePath) requirePath('list.derive.elsePath', derive.elsePath);
  }
  // A-R20: the two paths that carry content are declared like everything else.
  if (recipe.read?.bodyPath) requirePath('read.bodyPath', recipe.read.bodyPath);
  if (recipe.deeper) requirePath('deeper.bodyPath', recipe.deeper.bodyPath);

  // A slot names a declared label or a rail slot. Nothing else exists to fill it,
  // and the two sets are disjoint by the check above.
  const refsSlots = new Set<string>(REFS_SLOTS);
  const requireSlots = (where: string, template: string) => {
    for (const slot of templateSlots(template)) {
      if (refsSlots.has(slot) && !Object.hasOwn(recipe.card.refs, slot)) {
        errors.push(
          at(
            `${where} has the slot '{{${slot}}}', which only the card's refs can fill — declare refs.${slot}, or it renders to nothing and the call silently loses its argument`,
          ),
        );
        continue;
      }
      if (labelToPath.has(slot) || railSlots.has(slot)) continue;
      errors.push(
        at(
          `${where} has the slot '{{${slot}}}', which is neither a declared field label nor a slot the rail supplies`,
        ),
      );
    }
  };

  // A RESOLVE READS THE ITEM (A-R34). Its args may name a declared field label
  // and nothing else: a rail slot is the BEAT's fact, not this item's, and an
  // unfillable resolve is skipped at run time — so it is refused here instead,
  // where the recipe's author can still see it.
  for (const entry of recipe.resolve ?? []) {
    for (const [key, value] of Object.entries(entry.args)) {
      for (const text of argStrings(value)) {
        for (const slot of templateSlots(text)) {
          if (declaredLabels.has(slot)) continue;
          errors.push(
            at(
              `resolve '${entry.label}' fills '${key}' from '{{${slot}}}', which is not a declared field label — a resolve reads the item it is resolving and nothing else`,
            ),
          );
        }
      }
    }
  }

  requireSlots('card.title', recipe.card.title);
  if (recipe.card.link) requireSlots('card.link', recipe.card.link);
  for (const block of argBlocks(recipe)) {
    for (const [key, value] of Object.entries(block.args)) {
      for (const text of argStrings(value)) requireSlots(`${block.where}.${key}`, text);
    }
  }

  // Mode decides which half of the recipe must be there.
  if (recipe.mode === 'poll' && !recipe.list) {
    errors.push(at('mode is poll but there is no list — nothing says what to poll'));
  }
  if (recipe.mode === 'push' && !recipe.push) {
    errors.push(at('mode is push but there is no push block — nothing says where the item sits'));
  }
  if (recipe.list && (recipe.list.kind ?? 'tool') === 'tool') {
    if (!recipe.list.tool) errors.push(at('list.kind is tool but list.tool is missing'));
    if (recipe.list.itemsPath === undefined) {
      errors.push(at('list.kind is tool but list.itemsPath is missing'));
    }
  }
  if (recipe.card.gist === 'fields' && !recipe.card.fieldsForGist?.length) {
    errors.push(at('card.gist is fields but card.fieldsForGist names none'));
  }

  // A-R28: A RECIPE MAY NOT LIST MORE THAN IT CAN READ. The two caps look
  // independent and are not: the beat spends its reads on the first items and
  // then cards the rest WITHOUT opening them, and an occurred-at cursor moves
  // over every carded item — so the surplus is carded bodyless and never listed
  // again. The notes of a meeting, the text of a message: gone, silently, on the
  // one beat that could have read them. The numbers compared are the CLAMPED
  // ones, because those are what the beat will use: a recipe asking for 60 items
  // and 30 reads is really asking for 50 and 25.
  // A RESOLVE IS A READ TOO (A-R34), so it is counted here: the beat spends the
  // SAME budget on opening an item and on asking who its author is, and a recipe
  // whose per-item calls do not fit is the same defect wearing a longer sum. A
  // recipe with resolves and no read block is measured against the rail's own
  // ceiling, which is the only cap it has.
  // AN EXPANSION IS A CALL PER LISTED ROW (M3), and it is metered like any other,
  // so it belongs in this sum: a recipe whose expansion and resolves together
  // outrun its declared cap is a recipe whose stated worst case is a fiction.
  // The rail spends at most one expansion call per row it handles, which is the
  // same `maxItemsPerBeat` the items are counted against.
  const perItem = (recipe.read ? 1 : 0) + (recipe.resolve?.length ?? 0);
  const expandCalls = recipe.list?.expand?.maxRows ?? 0;
  const drainCalls = recipe.list?.drain?.maxPages ?? 0;
  if (perItem > 0 || expandCalls > 0 || drainCalls > 0) {
    const items = recipe.list?.maxItemsPerBeat ?? 0;
    const reads = Math.min(
      recipe.read?.maxReadsPerBeat ?? SENSE_CAPS.maxReadsPerBeat,
      SENSE_CAPS.maxReadsPerBeat,
    );
    // AN EXPANSION AND A DRAIN ARE CALLS TOO (M3, A-R91), and they are counted
    // by their OWN caps rather than per item: a beat opens up to
    // `expand.maxRows` containers and walks up to `drain.maxPages` pages down,
    // whatever the item cap says. A recipe whose stated worst case leaves them
    // out is a recipe whose stated worst case is a fiction.
    const calls = items * perItem + expandCalls + drainCalls;
    if (calls > reads) {
      // ONE SENTENCE, and the recipe's author is the reader. A recipe with no
      // resolve keeps the sentence it always had (two caps, named); one WITH a
      // resolve needs the arithmetic spelled out, because neither cap on its own
      // explains the number.
      const legs = [
        ...(recipe.read ? ['a read'] : []),
        ...(recipe.resolve?.length ? [`${recipe.resolve.length} resolve(s)`] : []),
      ];
      const extras = [
        ...(expandCalls > 0 ? [`${expandCalls} expansion(s)`] : []),
        ...(drainCalls > 0 ? [`${drainCalls} drain page(s)`] : []),
      ];
      const spend =
        recipe.resolve?.length || extras.length > 0
          ? `${items} row(s) at ${perItem} call(s) each (${legs.join(' plus ') || 'nothing'})${extras.length > 0 ? `, plus ${extras.join(' and ')},` : ''} is ${calls} calls against a read cap of ${reads}`
          : `list.maxItemsPerBeat is ${items} but read.maxReadsPerBeat is ${reads}`;
      errors.push(
        at(
          `${spend} — the beat would card ${items - Math.floor(reads / perItem)} item(s) without opening them, and the cursor advances over every carded item, so their bodies are never read`,
        ),
      );
    }
  }

  // A-R93, HALF ONE: `morePath` and `drain` BELONG TO A NEWEST-FIRST LIST.
  //
  // On an oldest-first list the rows a page did not send lie ABOVE it, which the
  // next beat reaches simply by advancing — so the executor ignores both fields
  // there, and a field the rail ignores reads to the next author as a guarantee
  // it is not. Refused for every recipe, shipped or not: it is a statement about
  // the rail, not about who wrote the recipe.
  if (recipe.list?.order === 'oldest-first' && recipe.list.windowGranularity === undefined) {
    if (recipe.list.morePath !== undefined || recipe.list.drain !== undefined) {
      errors.push(
        at(
          "list.morePath and list.drain belong to a 'newest-first' list — on an oldest-first one the rows a page did not send are ABOVE it and the next beat reaches them by advancing, so the rail ignores both and a reader would take them for a guarantee; declare list.windowGranularity if this list's own window argument is coarser than its cursor and cannot advance with it",
        ),
      );
    }
  }
  // A-R110 (Minor 4): AND THE EXCEPTION DOES NOT COVER BOTH FIELDS EQUALLY.
  // `drain` and `morePath` are not the same kind of thing. A `row` or `answer`
  // drain STOPS on `morePath`, so under a coarse window it is load-bearing. A
  // page-number drain has the page numbers for its whole answer and never reads
  // it, and with no drain at all nothing reads it — so there it is a second
  // signal that can disagree with the first, which is precisely the guarantee
  // the rail does not honour that this rule exists to refuse.
  if (
    recipe.list?.order === 'oldest-first' &&
    recipe.list.windowGranularity !== undefined &&
    recipe.list.morePath !== undefined &&
    !(
      recipe.list.drain !== undefined &&
      ('row' in recipe.list.drain.from || 'answer' in recipe.list.drain.from)
    )
  ) {
    errors.push(
      at(
        "list.morePath on an oldest-first list is read only by a drain that asks for it — one whose `from` is `row` or `answer`. Beside a page-number drain the page numbers are the whole answer, and with no drain nothing reads it at all, so the field would read as a guarantee the rail does not honour",
      ),
    );
  }
  // A-R107: AND THE EXCEPTION IS NOT FREE EITHER. A coarse window is a statement
  // about a TIME cursor: a token feed's page IS its cursor, so there is no
  // granularity gap to declare, and a recipe saying so is describing something
  // that cannot happen.
  if (recipe.list?.windowGranularity !== undefined && recipe.list.cursor.kind !== 'occurred-at') {
    errors.push(
      at(
        "list.windowGranularity says this list's window argument is coarser than its cursor, which only a time cursor has — a token cursor's page IS its position",
      ),
    );
  }

  // A-R17: A TOKEN CURSOR MUST ASK FOR A PAGE. A time cursor bounds its own
  // window, so a provider returning more than the beat can card is harmless —
  // the next beat asks again from the same time. A token cannot say "up to
  // here": whatever the page held is what the token skips past. So a recipe
  // that lets the provider choose the page size is a recipe that silently drops
  // whatever did not fit, and the rail's own cap becomes a lie.
  if (recipe.list && recipe.list.cursor.kind === 'token') {
    const asksForAPage = Object.values(recipe.list.args ?? {}).some((value) =>
      argStrings(value).some((text) => templateSlots(text).includes('pageSize')),
    );
    if (!asksForAPage) {
      errors.push(
        at(
          "list.cursor is a token but list.args never asks for '{{pageSize}}' — a token cursor skips past whatever the page held, so the page must be the rail's cap and not the provider's choice",
        ),
      );
    }
  }

  // ===========================================================================
  // THE RULES A USER RECIPE KEEPS AND A SHIPPED ONE DOES NOT
  //
  // A shipped recipe was read by a person before it merged; a user recipe was
  // written by an agent and approved field by field by someone who cannot be
  // expected to know what the card renderer does with a kind. So the two places
  // where a kind decides whether text reaches a durable row UNMASKED or
  // UNCLIPPED are refused here rather than trusted there.
  // ===========================================================================
  if (recipe.origin === 'user') {
    // A-R93, HALF TWO: A NEWEST-FIRST LIST ON A TIME CURSOR MUST SAY HOW IT IS
    // DRAINED — and this is the half that is a USER recipe's alone.
    //
    // Such a provider answers the newest rows above a lower bound, so the rows
    // it did not send lie BELOW the page, where moving the lower bound can never
    // reach them: the beat advances the cursor over rows it never listed and
    // loses them for good, in silence. The recipes route takes an agent-written
    // body for ANY app, so a history-shaped recipe on the next connector would
    // reproduce that defect in full and nothing would say so.
    //
    // WHY NOT EVERY RECIPE. Two shipped ones are newest-first on a time cursor
    // and neither is exposed, for reasons the rail cannot check. `gmail-inbox`
    // asks the provider for a hundred and cards twenty, so the whole window is
    // in hand and the beat drains it oldest-first itself — which argument is the
    // provider's page size is not something this validator can know.
    // `slack-my-threads` lists CONTAINERS: its cursor tracks the replies it
    // carded, never the matches it listed, and the thread it did not open this
    // beat is opened next beat over the same week window. A shipped recipe is
    // code somebody read before it merged, which is the same reason the three
    // rules below are a user recipe's alone.
    if (
      recipe.list?.order === 'newest-first' &&
      recipe.list.cursor.kind === 'occurred-at' &&
      (recipe.list.morePath === undefined || recipe.list.drain === undefined)
    ) {
      errors.push(
        at(
          "list.order is 'newest-first' on a time cursor, so the rows a page did not send are BELOW it and the cursor can never reach them by moving forward — declare list.morePath (where this provider says more remain) and list.drain (how to ask it for the next page down), or list oldest-first",
        ),
      );
    }

    // A-R71: the two shapes the table enforces, before anything spends.
    if (!USER_NAME_SHAPE.test(recipe.name)) {
      errors.push(
        at(
          `name is '${recipe.name}', and a user recipe's name is a slug of 2 to 64 characters: lower-case letters, digits and hyphens, starting with a letter or a digit`,
        ),
      );
    }
    if (!USER_APP_SHAPE.test(recipe.app)) {
      errors.push(
        at(
          `app is '${recipe.app}', and a user recipe's app is the connected toolkit's own slug: lower-case letters, digits and underscores`,
        ),
      );
    }

    // A-R66: A RECIPE'S TOOLS BELONG TO ITS APP, read off the slug's own prefix.
    // The route asks the app which toolkit a slug is in, which is the answer
    // that decides; this is the same fact for free, before a single round trip.
    // It is worth having twice because the mistake is an honest one: the agent
    // finds tools with a search that spans toolkits, so a Gmail slug can land on
    // a recipe approved as "watch github" — and it would then read Gmail under
    // github's consent row, github's flag and github's evidence door.
    const prefix = `${recipe.app.toUpperCase()}_`;
    for (const call of toolCalls(recipe)) {
      if (call.tool.toUpperCase().startsWith(prefix)) continue;
      errors.push(
        at(
          `${call.where} is '${call.tool}', which is not one of ${recipe.app}'s tools — a sense reads the app it was approved for, and nothing else`,
        ),
      );
    }

    // A resolve's answer is a virtual field of kind `person` under its own
    // label (A-R34), so it is in this map for the same reason it is in
    // `senseFields`: the card cannot tell the two apart, and neither may this.
    const kindByPath = new Map<string, FieldKind>(recipe.fields.map((f) => [f.path, f.kind]));
    for (const entry of recipe.resolve ?? []) kindByPath.set(entry.label, entry.kind);

    // A PERSON DOES NOT BELONG IN THE GIST. `gistField` renders a person through
    // `renderPerson` and does NOT clip the result (every other kind is cut at
    // 200 chars), so one `attendees`-shaped person field prints a whole roster
    // into the card body. The Who line is where people go, and it is capped.
    recipe.card.fieldsForGist?.forEach((path, i) => {
      if (kindByPath.get(path) !== 'person') return;
      errors.push(
        at(
          `card.fieldsForGist[${i}] is '${path}', a person field — people belong on the card's who line, which is capped; a person in the gist is rendered unclipped`,
        ),
      );
    });

    // A REF IS AN ID, A LINK OR A PERSON — never free text. Refs are stored
    // VERBATIM so the citation they mint can be clicked (A-R53), which means a
    // text ref writes the raw subject of a mail into an immutable row with no
    // address mask over it.
    for (const [key, path] of Object.entries(recipe.card.refs)) {
      if (kindByPath.get(path) !== 'text') continue;
      errors.push(
        at(
          `card.refs.${key} is '${path}', a text field — a ref is stored verbatim and never masked, so it may only be an id, a link or a person`,
        ),
      );
    }
  }

  // A named parser is code we wrote for one provider. A user recipe gets json.
  if (recipe.origin === 'user') {
    for (const [where, parse] of [
      ['list.parse', recipe.list?.parse],
      ['read.parse', recipe.read?.parse],
    ] as const) {
      if (parse && parse !== 'json') {
        errors.push(
          at(
            `${where} is '${parse}', and a user recipe may only parse json — a named parser is shipped code, not something a recipe may reach for`,
          ),
        );
      }
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, recipe };
}
